import { type FunctionComponent, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from 'src/components/atoms/Avatar/Avatar';
import { Button } from 'src/components/atoms/Button/Button';
import style from './AccountMenu.module.scss';

interface Props {
  name: string;
  email?: string;
  picture?: string;
  onSignOut: () => void;
}

export const AccountMenu: FunctionComponent<Props> = ({ name, email, picture, onSignOut }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  const panel = useId();

  useEffect(() => {
    if (!open) {
      return;
    }

    const closeOutside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        toggle.current?.focus();
      }
    };

    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  return (
    <div ref={root} className={style.menu}>
      <button
        ref={toggle}
        type="button"
        aria-label={t('header.account')}
        aria-expanded={open}
        aria-controls={panel}
        className={style.toggle}
        onClick={() => setOpen(!open)}
      >
        <Avatar name={name} picture={picture} />
      </button>
      {open ? (
        <div id={panel} className={style.panel}>
          <p className={style.who}>
            <span className={style.name}>{name}</span>
            {email && email !== name ? <span className={style.email}>{email}</span> : null}
          </p>
          <Button
            variant="ghost"
            aria-haspopup="dialog"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
          >
            {t('header.signOut')}
          </Button>
        </div>
      ) : null}
    </div>
  );
};
